/**
 * Navigation utilities for case workspace.
 * Provides back-navigation and recent items history.
 */

import { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { create } from "zustand";

interface RecentItem {
  path: string;
  label: string;
  caseId?: string;
  visitedAt: number;
}

interface NavigationState {
  recentItems: RecentItem[];
  addRecent: (item: Omit<RecentItem, "visitedAt">) => void;
  clearRecent: () => void;
}

const MAX_RECENT = 20;

export const useNavigationStore = create<NavigationState>((set) => ({
  recentItems: [],
  addRecent: (item) =>
    set((s) => {
      // Deduplicate by path, move to front
      const filtered = s.recentItems.filter((r) => r.path !== item.path);
      return {
        recentItems: [{ ...item, visitedAt: Date.now() }, ...filtered].slice(0, MAX_RECENT),
      };
    }),
  clearRecent: () => set({ recentItems: [] }),
}));

export function useNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const addRecent = useNavigationStore((s) => s.addRecent);
  const recentItems = useNavigationStore((s) => s.recentItems);

  /** Navigate back: if inside a case, go to the workspace landing; otherwise
   *  go home. (N1: /cases is retired — /workspace is the all-cases surface.) */
  const goBack = useCallback(() => {
    const path = location.pathname;
    if (/^\/cases\/[^/]+/.test(path)) {
      navigate("/workspace");
    } else if (path !== "/home") {
      navigate("/home");
    }
  }, [location.pathname, navigate]);

  /** Navigate to a case workspace and record it in recent items */
  const openCase = useCallback(
    (caseId: string, title: string) => {
      const path = `/cases/${caseId}`;
      addRecent({ path, label: title, caseId });
      navigate(path);
    },
    [navigate, addRecent],
  );

  /** Navigate to a sub-section within the current case */
  const openCaseSection = useCallback(
    (caseId: string, section: string, label: string) => {
      const path = `/cases/${caseId}/${section}`;
      addRecent({ path, label, caseId });
      navigate(path);
    },
    [navigate, addRecent],
  );

  return { goBack, openCase, openCaseSection, recentItems };
}
